/* eslint-disable @typescript-eslint/no-floating-promises */
import {
  AfterViewChecked, AfterViewInit, Component, ElementRef, Inject, OnInit, QueryList, ViewChild, ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import { ModalComponent } from '../../modal/modal.component';
import { Countries, EeocSources } from '../../enums';
import { EeocFormData, Environment, LookupFieldData, Person, User } from '../../../typings';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EeocApiService } from '../eeoc-api.service';
import { ApiService } from '../../api.service';
import { FeatureService, TalentAppFeatures } from '../../service/feature.service';
import { EnvironmentToken } from '../../../util';
import { TranslateService } from '@ngx-translate/core';
import { SharedDataService } from '../../gather/gather-services/shared-data.service';
import { Gather } from '../../gather/gather-typings';

@Component({
    selector: 'app-eeoc-form',
    templateUrl: './eeoc-form.component.html',
    styleUrls: ['./eeoc-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EeocFormComponent implements OnInit, AfterViewInit, AfterViewChecked {
  @ViewChild(ModalComponent) deleteEeocModal;

  @ViewChildren('selectElement') selectElementList!: QueryList<ElementRef>;

  readonly GENDER_DID_NOT_DISCLOSE_ID = 3;
  readonly ETHNICITY_DID_NOT_DISCLOSE_ID = 1;
  readonly ETHNICITY_OTHER_ID = 12;
  readonly GENDER_OTHER_ID = 8;

  source = '';
  sources = Object.values(EeocSources);

  genders: LookupFieldData[];
  ethnicityList: LookupFieldData[];
  ageGroupList: LookupFieldData[];

  hideLgbtqDetails = true;
  hideEthnicityDetails = true;
  hideVeteranDetails = true;
  hideDisabilityDetails = true;
  hideNeuroDetails = true;

  formGroup: UntypedFormGroup;
  submitted = false;
  talentPersonId: number;
  postingId: number;
  talentName: string;
  displayForm = false;
  redirectToProfile = false;
  redirectToApply = false;
  ageGroupFeature = true;
  redirectUrl = '';
  division = '';
  talentCountryId: number;
  country = Countries;
  uuid = '';
  gather: Gather;
  selectElementWidth = 'auto';

  constructor(
    protected route: ActivatedRoute,
    protected formBuilder: UntypedFormBuilder,
    protected eeocApi: EeocApiService,
    protected apiService: ApiService,
    protected router: Router,
    protected featureService: FeatureService,
    protected translateService: TranslateService,
    private gatherSharedDataService: SharedDataService,
    @Inject(EnvironmentToken) protected environment: Environment
  ) {
    this.setParams();
  }

  private setParams(): void {
    this.route.queryParams.subscribe(
      params => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        if (this.sources.indexOf(params.source) >= 0) {
          this.source = params.source;
          this.setGather();
        }
        if (params.redirectToProfile) {
          this.redirectToProfile = params.redirectToProfile === 'true';
        }
        if (params.redirectToApply) {
          this.redirectToApply = params.redirectToApply === 'true';
        }
        if (params.redirectUrl) {
          this.redirectUrl = params.redirectUrl;
        }
        if (params.division) {
          this.division = params.division;
        }
        if (params.postingId) {
          this.postingId = params.postingId;
        }
      },
      error => {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        console.error(`Error getting lookup fields: ${error}`);
      }
    );
  }

  private setGather(): void {
    if (this.source === EeocSources.GATHER) {
      this.gatherSharedDataService.gather.subscribe(gather => {
        this.gather = gather;
      });
    }
  }

  ngOnInit(): void {
    // If we actually have a uuid parameter, do this
    this.uuid = this.getUuid();
    if (this.uuid !== null && this.uuid !== '') {
      this.getUserInfoByEeocUuid();
    }
    // otherwise, pull user info based on authentication
    else {
      this.getUserInfoByAuthentication();
    }
    this.ageGroupFeature = this.featureService.getFeature(TalentAppFeatures.AGE_GROUP);
  }

  ngAfterViewInit(): void {
    // eslint-disable-next-line
    // @ts-ignore
    hj('event', 'eeoc_form_viewed'); // eslint-disable-line
  }

  ngAfterViewChecked(): void {
    if (this.selectElementWidth == 'auto' && this.selectElementList.length != 0) {
      this.setUniformSelectElementWidth();
    }
  }

  // This should only be used for people that have never answered EEOC form before
  private getUserInfoByEeocUuid(): void {
    this.eeocApi.getPersonByEeocUuid(this.uuid).subscribe(
      person => {
        if (person.id > 0) {
          this.setPersonInfo(person);
          this.setLanguage();
          this.initializeFormGroup();
        } else {
          if (this.source === EeocSources.REDEPLOYME) {
            this.router.navigate(['preferences-confirmed', this.uuid], { queryParams: { updatePreferences: false } } );
          } else {
            top.location.replace(this.environment.matHost);
          }
        }
      },
      error => {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        console.error(`Error retrieving person by uuid: ${error}`);
      },
      () => {
        this.getEeocLookupFields(this.talentPersonId);
      }
    );
  }

  private getUserInfoByAuthentication(): void {
    this.apiService.getUserInfo(false).subscribe(
      person => {
        this.setPersonInfo(person);
        this.setLanguage();
        this.populateEeoc();
        this.initializeFormGroup();
      },
      error => {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        console.error(`Error retrieving logged in user's info: ${error}`);
      },
      () => {
        this.getEeocLookupFields(this.talentPersonId);
      }
    );
  }

  private setPersonInfo(person: User | Person): void {
    this.talentPersonId = person.id;
    this.talentName = person.preferredName === null || person.preferredName === '' ? person.firstName : person.preferredName;
    this.talentCountryId = person.homeMarketCountryId;
  }

  initializeFormGroup() {
    let required: ValidatorFn = null;
    if (this.talentCountryId == Countries.USA) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      required = Validators.required;
    }
    this.formGroup = this.formBuilder.group({
      gender: new UntypedFormControl('', required),
      lgbtq: new UntypedFormControl('', required),
      ethnicity: new UntypedFormControl('', required),
      ageGroup: new UntypedFormControl('', required),
      veteranIdentification: new UntypedFormControl('', required),
      selfIdentificationDisability: new UntypedFormControl('', required),
      neurodivergent: new UntypedFormControl('', required),
      authorizedToWorkInUS: new UntypedFormControl('', required),
      requiresSponsorship: new UntypedFormControl('', required),
      customGender: new UntypedFormControl('')
    });
  }

  getEeocLookupFields(talentId: number) {
    this.eeocApi.getEeocLookupFields(talentId).subscribe(response => {
      const orderKeyComparator = (item1: LookupFieldData, item2: LookupFieldData): number => {
        const compare = item1.orderKey - item2.orderKey;
        if (compare !== 0) {
          return compare;
        } else if (item1.description < item2.description) {
          return -1;
        } else if (item2.description < item1.description) {
          return 1;
        }

        return 0;
      };

      this.genders = response['gender'].sort(orderKeyComparator);
      this.ethnicityList = response['ethnicity'].sort(orderKeyComparator);
      this.ageGroupList = response['ageGroup'].sort(orderKeyComparator);
      this.displayForm = true;
      this.updateSelectOptions();
    });

    this.route.queryParams.subscribe(
      params => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        if (this.sources.indexOf(params.source) >= 0) {
          this.source = params.source;
        }
        if (params.redirectToProfile) {
          this.redirectToProfile = params.redirectToProfile === 'true';
        }
        if (params.redirectToApply) {
          this.redirectToApply = params.redirectToApply === 'true';
        }
        if (params.redirectUrl) {
          this.redirectUrl = params.redirectUrl;
        }
        if (params.division) {
          this.division = params.division;
        }
        if (params.postingId) {
          this.postingId = params.postingId;
        }
      },
      error => {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        console.error(`Error getting lookup fields: ${error}`);
      }
    );
  }

  /* This method gets updated in subclass -- in this class it's just a stub that does nothing. */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateSelectOptions() {}

  populateEeoc() {
    this.eeocApi.readEeocData(this.talentPersonId).subscribe((response: EeocFormData) => {
      const allowedGenderIds = this.genders.map(lookupField => lookupField.id);
      const allowedAgeGroupIds = this.ageGroupList.map(lookupField => lookupField.id);
      const allowedEthnicityIds = this.ethnicityList.map(lookupField => lookupField.id);
      this.formGroup.get('gender').patchValue(allowedGenderIds.includes(response.eeoc.gender) ? response.eeoc.gender : '');
      this.formGroup.get('lgbtq').patchValue(response.eeoc.lgbtqIdentification);
      this.formGroup.get('ethnicity').patchValue(allowedEthnicityIds.includes(response.eeoc.ethnicity) ? response.eeoc.ethnicity : '');
      this.formGroup.get('ageGroup').patchValue(allowedAgeGroupIds.includes(response.eeoc.ageGroup) ? response.eeoc.ageGroup : '');
      this.formGroup.get('veteranIdentification').patchValue(response.eeoc.veteranIdentification);
      this.formGroup.get('selfIdentificationDisability').patchValue(response.eeoc.disabilityIdentification);
      this.formGroup.get('neurodivergent').patchValue(response.eeoc.neurodivergentIdentification);
      this.formGroup.get('customGender').patchValue(response.eeoc.customGender);
      this.formGroup.get('authorizedToWorkInUS').patchValue(response.authorizedToWorkInUS);
      this.formGroup.get('requiresSponsorship').patchValue(response.requiresSponsorship);
    });
  }

  submitEeoc() {
    this.submitted = true;
    if (this.formGroup.valid) {
      const request: EeocFormData = {
        talentId: this.talentPersonId,
        formId: this.uuid,
        homeMarketCountryId: this.talentCountryId,
        authorizedToWorkInUS: this.formGroup.get('authorizedToWorkInUS').value,
        requiresSponsorship: this.formGroup.get('requiresSponsorship').value,
        eeoc: {
          gender: this.formGroup.get('gender').value,
          lgbtqIdentification: this.formGroup.get('lgbtq').value,
          ethnicity: this.formGroup.get('ethnicity').value,
          veteranIdentification: this.formGroup.get('veteranIdentification').value,
          disabilityIdentification: this.formGroup.get('selfIdentificationDisability').value,
          neurodivergentIdentification: this.formGroup.get('neurodivergent').value,
          ageGroup: this.ageGroupFeature ? this.formGroup.get('ageGroup').value : null,
          updateSource: this.source,
          customGender: this.formGroup.get('gender').value == this.GENDER_OTHER_ID ? this.formGroup.get('customGender').value : null
        }
      };
      this.eeocApi.submitEeocData(request).subscribe(() => {
        if (this.source === EeocSources.GATHER) {
          this.router.navigate(['gather', this.route.parent.snapshot.paramMap.get('trackingId'), 'confirm'], {
            queryParams: { fromRoute: 'interested' }
          });
        } else if (this.source === EeocSources.REDEPLOYME) {
          this.router.navigate(['eeoc/success'], { queryParams: { source: EeocSources.REDEPLOYME } });
        } else if (this.source != EeocSources.EMAIL) {
          const isApplication = this.source === EeocSources.APPLICATION;
          const isRegistration = this.source === EeocSources.REGISTRATION;
          let path = `myaquent/registration/talent/registration/success?isApplication=${String(isApplication)}&isRegistration=${String(
            isRegistration
          )}`;
          if (this.redirectToProfile) {
            path = 'myaquent/profile?success=eeoc';
          }
          top.location.replace(`${this.environment.matHost}/${path}`);
        } else {
          this.router.navigate(['eeoc/success']);
        }
      });
    }
  }

  get formControls() {
    return this.formGroup.controls;
  }

  changeDetailsBoxVisibility(detailsName, toggle) {
    switch (detailsName) {
      case 'lgbtq': {
        this.hideLgbtqDetails = toggle;
        break;
      }
      case 'ethnicity': {
        this.hideEthnicityDetails = toggle;
        break;
      }
      case 'veteran': {
        this.hideVeteranDetails = toggle;
        break;
      }
      case 'disability': {
        this.hideDisabilityDetails = toggle;
        break;
      }
      case 'neurodivergent': {
        this.hideNeuroDetails = toggle;
        break;
      }
      default: {
        break;
      }
    }
  }

  openDeleteEeocModal() {
    this.deleteEeocModal.show();
  }

  closeDeleteEeocModal() {
    this.deleteEeocModal.hide();
  }

  setLanguage() {
    if (this.talentCountryId == Countries.UK) {
      this.translateService.use('en_uk');
    } else if (this.talentCountryId == Countries.NL) {
      this.translateService.use('en_nl');
    } else if (this.talentCountryId == Countries.CA) {
      this.translateService.use('en_ca');
    } else if (this.talentCountryId == Countries.AU) {
      this.translateService.use('en_au');
    }
  }

  private getUuid(): string {
    if (this.gather) {
      return this.gather.eeocFormUuid;
    } else {
      return this.route.snapshot.paramMap.get('uuid');
    }
  }

  private setUniformSelectElementWidth(): void {
    const selectElementWidthList = this.selectElementList.map(e => e.nativeElement.offsetWidth);
    this.selectElementWidth = selectElementWidthList.sort().pop() + 'px';
  }
}
